import React, { useState, useEffect, useCallback } from 'react';
import {
  Page,
  Layout,
  Card,
  FormLayout,
  TextField,
  Select,
  Checkbox,
  Button,
  BlockStack,
  InlineStack,
  Text,
  Divider,
  Badge,
  Toast,
  Banner,
  Box,
} from '@shopify/polaris';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../api';

const ATTRIBUTE_TYPES = [
  { label: 'Text Box (single line)', value: 'text' },
  { label: 'Text Area (multi-line)', value: 'textarea' },
  { label: 'Number', value: 'number' },
  { label: 'Dropdown (select one)', value: 'dropdown' },
  { label: 'Radio Buttons', value: 'radio' },
  { label: 'Checkbox (yes/no)', value: 'checkbox' },
  { label: 'Multi-Select (choose many)', value: 'multiselect' },
  { label: 'Color Swatch', value: 'color' },
  { label: 'Date Picker', value: 'date' },
  { label: 'File Upload', value: 'file' },
  { label: 'Toggle Switch', value: 'toggle' },
  { label: 'List', value: 'list' },
];

const TYPES_WITH_OPTIONS = ['dropdown', 'radio', 'checkbox', 'multiselect', 'color', 'list'];

export default function AttributeForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [form, setForm] = useState({
    name: '',
    label: '',
    type: 'text',
    placeholder: '',
    default_value: '',
    help_text: '',
    is_required: false,
    is_active: true,
    sort_order: 0,
    group_id: '',
    validation_rules: { min: '', max: '', pattern: '' },
  });
  const [options, setOptions] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    // Load groups
    api.get('/api/attributes/groups').then(res => {
      setGroups(res.data?.data || []);
    }).catch(() => {});

    // Load attribute if editing
    if (isEdit) {
      setLoading(true);
      api.get(`/api/attributes/${id}`).then(res => {
        const attr = res.data?.data;
        if (attr) {
          setForm({
            name: attr.name || '',
            label: attr.label || '',
            type: attr.type || 'text',
            placeholder: attr.placeholder || '',
            default_value: attr.default_value || '',
            help_text: attr.help_text || '',
            is_required: !!attr.is_required,
            is_active: attr.is_active !== false,
            sort_order: attr.sort_order || 0,
            group_id: attr.group_id ? String(attr.group_id) : '',
            validation_rules: attr.validation_rules || { min: '', max: '', pattern: '' },
          });
          setOptions(attr.options || []);
        }
      }).catch(() => {
        setToast({ content: 'Failed to load attribute', error: true });
      }).finally(() => setLoading(false));
    }
  }, [id, isEdit]);

  // Auto-generate name from label
  const handleLabelChange = (value) => {
    setForm(f => ({
      ...f,
      label: value,
      name: !isEdit ? value.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '') : f.name,
    }));
  };

  const validate = () => {
    const errs = {};
    if (!form.label.trim()) errs.label = 'Label is required';
    if (!form.name.trim()) errs.name = 'Name is required';
    if (!/^[a-z0-9_]+$/.test(form.name)) errs.name = 'Name must be lowercase letters, numbers, underscores only';
    if (!form.type) errs.type = 'Type is required';
    if (TYPES_WITH_OPTIONS.includes(form.type) && options.length === 0) {
      errs.options = 'At least one option is required for this type';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        sort_order: parseInt(form.sort_order) || 0,
        group_id: form.group_id || null,
        options: TYPES_WITH_OPTIONS.includes(form.type) ? options : [],
      };

      if (isEdit) {
        await api.put(`/api/attributes/${id}`, payload);
        setToast({ content: 'Attribute updated successfully' });
      } else {
        await api.post('/api/attributes', payload);
        setToast({ content: 'Attribute created successfully' });
      }
      setTimeout(() => navigate('/attributes'), 1000);
    } catch (err) {
      setToast({ content: err.response?.data?.message || 'Failed to save attribute', error: true });
    } finally {
      setSaving(false);
    }
  };

  // Options management
  const addOption = () => {
    setOptions(prev => [...prev, { label: '', value: '', price_modifier: 0, color_swatch: '', sort_order: prev.length }]);
  };

  const updateOption = (index, field, value) => {
    setOptions(prev => prev.map((opt, i) => i === index ? { ...opt, [field]: value } : opt));
  };

  const removeOption = (index) => {
    setOptions(prev => prev.filter((_, i) => i !== index));
  };

  const groupOptions = [
    { label: 'No Group', value: '' },
    ...groups.map(g => ({ label: g.name, value: String(g.id) })),
  ];

  const showOptions = TYPES_WITH_OPTIONS.includes(form.type);

  const toastMarkup = toast ? (
    <Toast content={toast.content} error={toast.error} onDismiss={() => setToast(null)} />
  ) : null;

  return (
    <>
      {toastMarkup}
      <Page
        title={isEdit ? 'Edit Attribute' : 'Create Attribute'}
        subtitle={isEdit ? `Editing: ${form.label}` : 'Define a new custom product option'}
        backAction={{ content: 'Attributes', onAction: () => navigate('/attributes') }}
        primaryAction={{ content: saving ? 'Saving...' : 'Save Attribute', onAction: handleSave, loading: saving }}
        secondaryActions={[{ content: 'Cancel', onAction: () => navigate('/attributes') }]}
      >
        <Layout>
          {/* Basic Info */}
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <Text variant="headingMd" as="h2">Basic Information</Text>
                <FormLayout>
                  <FormLayout.Group>
                    <TextField
                      label="Label *"
                      value={form.label}
                      onChange={handleLabelChange}
                      error={errors.label}
                      helpText="Displayed to customers (e.g. 'Engraving Text')"
                      autoComplete="off"
                    />
                    <TextField
                      label="Name *"
                      value={form.name}
                      onChange={v => setForm(f => ({ ...f, name: v }))}
                      error={errors.name}
                      helpText="Internal identifier, lowercase with underscores (e.g. 'engraving_text')"
                      autoComplete="off"
                    />
                  </FormLayout.Group>

                  <Select
                    label="Type *"
                    options={ATTRIBUTE_TYPES}
                    value={form.type}
                    onChange={v => setForm(f => ({ ...f, type: v }))}
                    error={errors.type}
                    helpText="The input type shown to customers"
                  />

                  <Select
                    label="Group"
                    options={groupOptions}
                    value={form.group_id}
                    onChange={v => setForm(f => ({ ...f, group_id: v }))}
                    helpText="Optionally group related attributes together"
                  />

                  <FormLayout.Group>
                    <TextField
                      label="Placeholder"
                      value={form.placeholder}
                      onChange={v => setForm(f => ({ ...f, placeholder: v }))}
                      helpText="Hint text shown inside the input"
                      autoComplete="off"
                    />
                    <TextField
                      label="Default Value"
                      value={form.default_value}
                      onChange={v => setForm(f => ({ ...f, default_value: v }))}
                      helpText="Pre-filled value"
                      autoComplete="off"
                    />
                  </FormLayout.Group>

                  <TextField
                    label="Help Text"
                    value={form.help_text}
                    onChange={v => setForm(f => ({ ...f, help_text: v }))}
                    helpText="Additional instructions shown below the field"
                    autoComplete="off"
                  />

                  <TextField
                    label="Sort Order"
                    type="number"
                    value={String(form.sort_order)}
                    onChange={v => setForm(f => ({ ...f, sort_order: v }))}
                    helpText="Lower numbers appear first"
                    autoComplete="off"
                  />

                  <InlineStack gap="400">
                    <Checkbox
                      label="Required field"
                      checked={form.is_required}
                      onChange={v => setForm(f => ({ ...f, is_required: v }))}
                      helpText="Customers must fill this before adding to cart"
                    />
                    <Checkbox
                      label="Active"
                      checked={form.is_active}
                      onChange={v => setForm(f => ({ ...f, is_active: v }))}
                      helpText="Show this attribute on product pages"
                    />
                  </InlineStack>
                </FormLayout>
              </BlockStack>
            </Card>
          </Layout.Section>

          {/* Validation Rules */}
          {['text', 'textarea', 'number'].includes(form.type) && (
            <Layout.Section>
              <Card>
                <BlockStack gap="400">
                  <Text variant="headingMd" as="h2">Validation Rules</Text>
                  <FormLayout>
                    <FormLayout.Group>
                      <TextField
                        label={form.type === 'number' ? 'Minimum Value' : 'Min Length'}
                        type="number"
                        value={String(form.validation_rules?.min || '')}
                        onChange={v => setForm(f => ({ ...f, validation_rules: { ...f.validation_rules, min: v } }))}
                        autoComplete="off"
                      />
                      <TextField
                        label={form.type === 'number' ? 'Maximum Value' : 'Max Length'}
                        type="number"
                        value={String(form.validation_rules?.max || '')}
                        onChange={v => setForm(f => ({ ...f, validation_rules: { ...f.validation_rules, max: v } }))}
                        autoComplete="off"
                      />
                    </FormLayout.Group>
                    {form.type === 'text' && (
                      <TextField
                        label="Pattern (regex)"
                        value={form.validation_rules?.pattern || ''}
                        onChange={v => setForm(f => ({ ...f, validation_rules: { ...f.validation_rules, pattern: v } }))}
                        helpText="e.g. ^[A-Za-z]+$ for letters only"
                        autoComplete="off"
                      />
                    )}
                  </FormLayout>
                </BlockStack>
              </Card>
            </Layout.Section>
          )}

          {/* Options (for dropdown, radio, etc.) */}
          {showOptions && (
            <Layout.Section>
              <Card>
                <BlockStack gap="400">
                  <InlineStack align="space-between" blockAlign="center">
                    <Text variant="headingMd" as="h2">Options</Text>
                    <Button onClick={addOption} size="slim">+ Add Option</Button>
                  </InlineStack>

                  {errors.options && (
                    <Banner tone="critical">{errors.options}</Banner>
                  )}

                  {options.length === 0 ? (
                    <Box padding="400" background="bg-surface-secondary" borderRadius="200">
                      <Text tone="subdued" alignment="center">No options yet. Click "+ Add Option" to add choices.</Text>
                    </Box>
                  ) : (
                    <BlockStack gap="300">
                      {options.map((opt, index) => (
                        <Card key={index} background="bg-surface-secondary">
                          <InlineStack gap="300" blockAlign="end" wrap>
                            <div style={{ flex: 2, minWidth: 120 }}>
                              <TextField
                                label={index === 0 ? 'Label' : ''}
                                value={opt.label}
                                onChange={v => {
                                  updateOption(index, 'label', v);
                                  updateOption(index, 'value', v.toLowerCase().replace(/[^a-z0-9]+/g, '_'));
                                }}
                                placeholder="Option label"
                                autoComplete="off"
                              />
                            </div>
                            <div style={{ flex: 2, minWidth: 120 }}>
                              <TextField
                                label={index === 0 ? 'Value' : ''}
                                value={opt.value}
                                onChange={v => updateOption(index, 'value', v)}
                                placeholder="option_value"
                                autoComplete="off"
                              />
                            </div>
                            <div style={{ flex: 1, minWidth: 100 }}>
                              <TextField
                                label={index === 0 ? 'Price Modifier' : ''}
                                type="number"
                                value={String(opt.price_modifier || 0)}
                                onChange={v => updateOption(index, 'price_modifier', parseFloat(v) || 0)}
                                prefix="+"
                                autoComplete="off"
                              />
                            </div>
                            {form.type === 'multiselect' && (
                              <div style={{ flex: 2, minWidth: 140 }}>
                                <TextField
                                  label={index === 0 ? 'Image URL' : ''}
                                  value={opt.image_url || ''}
                                  onChange={v => updateOption(index, 'image_url', v)}
                                  placeholder="https://..."
                                  autoComplete="off"
                                />
                              </div>
                            )}
                            {form.type === 'color' && (
                              <div style={{ flex: 1, minWidth: 80 }}>
                                <TextField
                                  label={index === 0 ? 'Color' : ''}
                                  type="color"
                                  value={opt.color_swatch || '#000000'}
                                  onChange={v => updateOption(index, 'color_swatch', v)}
                                  autoComplete="off"
                                />
                              </div>
                            )}
                            <div style={{ paddingTop: index === 0 ? '22px' : '0' }}>
                              <Button tone="critical" size="slim" onClick={() => removeOption(index)}>Remove</Button>
                            </div>
                          </InlineStack>
                        </Card>
                      ))}
                    </BlockStack>
                  )}
                </BlockStack>
              </Card>
            </Layout.Section>
          )}

          {/* Save Actions */}
          <Layout.Section>
            <InlineStack gap="300" align="end">
              <Button onClick={() => navigate('/attributes')}>Cancel</Button>
              <Button variant="primary" onClick={handleSave} loading={saving}>
                {isEdit ? 'Update Attribute' : 'Create Attribute'}
              </Button>
            </InlineStack>
          </Layout.Section>
        </Layout>
      </Page>
    </>
  );
}
